import { FC, useEffect, useMemo, useState } from "react";
import { useAppSelector } from "../../../app/store/store";
import { idmService, IIdmStaffListItem } from "../../../entities/idm";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { useMyActions } from "../../../entities/my";
import { ILocalDate } from "../../../entities/calendar";
import { shiftService, IShift, IShiftTeamInput } from "../../../entities/shift";
import { ITeam, ITeamStaff, teamService } from "../../../entities/team";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { MyError } from "../../../shared/lib/error/MyError";
import { IItem } from "../../../shared/model/types";
import { MyButton, MyButtonSecondary } from "../../../shared/ui/button";
import { DropDownListSelected } from "../../../shared/ui/dropDownSelected";
import { Modal } from "../../../shared/ui/modal";
import { LoaderSpinner } from "../../../shared/ui/spinner";
import classes from './shiftAssign.module.scss'

interface IProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    date: ILocalDate;
    shift?: IShift;
    workTgIds?: number[];
    onSuccess: (shift: IShift) => void;
}

interface ITeamFormValue {
    coordinatorUserId: number;
    curatorUserIdsByTgId: Record<number, number>;
}

const staffToItem = (staff: IIdmStaffListItem): IItem => ({
    id: staff.user_id,
    name: `${staff.user_id}. ${staff.full_name}`.trim(),
})

const teamStaffToItem = (staff: ITeamStaff): IItem => ({
    id: Number(staff.userId),
    name: `${staff.userId}. ${staff.fullName}`.trim(),
})

const uniqueItems = (items: IItem[]): IItem[] => {
    const map = new Map<number | string, IItem>()
    items.forEach(item => {
        if(item.id) map.set(item.id, item)
    })

    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name))
}

const getDefaultCoordinatorId = (team: ITeam): number => Number(team.coordinators[0]?.userId ?? 0)

const getDefaultCuratorId = (team: ITeam, tgId: number): number => {
    return Number(team.tgCurators.find(item => Number(item.tgAccount.id) === tgId)?.curators[0]?.userId ?? 0)
}

const getTeamShiftValue = (team: ITeam, shift?: IShift): ITeamFormValue => {
    const shiftTeam = shift?.teams.find(item => item.teamId === team.id)
    const curatorUserIdsByTgId: Record<number, number> = {}

    team.tgCurators.forEach(tgCurators => {
        const tgId = Number(tgCurators.tgAccount.id)
        const shiftCurator = shiftTeam?.curators.find(item => Number(item.tgAccount.id) === tgId)
        const plannedShiftUserId = Number(shiftCurator?.curator.plannedStaff?.userId ?? 0)

        curatorUserIdsByTgId[tgId] = plannedShiftUserId || getDefaultCuratorId(team, tgId)
    })

    return {
        coordinatorUserId: Number(shiftTeam?.coordinator.plannedStaff?.userId ?? getDefaultCoordinatorId(team)),
        curatorUserIdsByTgId,
    }
}

export const ShiftAssign: FC<IProps> = ({open, setOpen, date, shift, workTgIds = [], onSuccess}) => {

    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()
    const {my} = useAppSelector(state => state.myReducer)

    const [teams, setTeams] = useState<ITeam[]>([])
    const [headTeacherOptions, setHeadTeacherOptions] = useState<IItem[]>([])
    const [selectedTeamIds, setSelectedTeamIds] = useState<number[]>([])
    const [teamValues, setTeamValues] = useState<Record<number, ITeamFormValue>>({})
    const [headTeacherId, setHeadTeacherId] = useState<number>(0)
    const [isLoadingOptions, setIsLoadingOptions] = useState<boolean>(false)
    const [isSaving, setIsSaving] = useState<boolean>(false)
    const [error, setError] = useState<string>('')

    const title = shift ? 'Изменить плановый состав смены' : 'Назначить смену'
    const workTgIdsKey = useMemo(() => workTgIds.join(','), [workTgIds])
    const currentUserHeadTeacherOption = useMemo<IItem | null>(() => {
        if(my.role !== 'assistant' || my.id <= 0) return null

        return {
            id: my.id,
            name: `${my.id}. ${my.full_name}`.trim(),
        }
    }, [my.full_name, my.id, my.role])
    const shiftHeadTeacherOption = useMemo<IItem | null>(() => {
        const plannedStaff = shift?.headTeacher.plannedStaff
        const userId = Number(plannedStaff?.userId ?? 0)

        if(!userId) return null

        return {
            id: userId,
            name: `${userId}. ${plannedStaff?.fullName ?? ''}`.trim(),
        }
    }, [shift?.headTeacher.plannedStaff])

    const selectedTeamIdItems = useMemo(() => selectedTeamIds, [selectedTeamIds])

    const handleError = (e: unknown, defaultMessage: string) => {
        console.log(e)

        if(e instanceof AuthError){
            setIsAuth(false)
            setGlobalMessage({message: e.message, type: 'error'})
            return
        }

        if(e instanceof MyError){
            setGlobalMessage({message: e.message, type: 'error'})
            return
        }

        setGlobalMessage({message: defaultMessage, type: 'error'})
    }

    useEffect(() => {
        if(!open) return

        let isActual = true

        const loadHeadTeacherOptions = async (): Promise<{items: IItem[]; useCurrentUserAsDefault: boolean}> => {
            let staffItems: IItem[] = []

            try{
                const roles = await idmService.listRoles()
                const headTeacherRoleId = roles.find(role => role.code === 'assistant')?.id

                if(headTeacherRoleId){
                    const staffResponse = await idmService.listStaff([headTeacherRoleId])
                    staffItems = staffResponse.items.map(staffToItem)
                }
            }
            catch(e){
                if(!currentUserHeadTeacherOption){
                    throw e
                }
            }

            return {
                items: uniqueItems([
                    ...staffItems,
                    ...(shiftHeadTeacherOption ? [shiftHeadTeacherOption] : []),
                    ...(currentUserHeadTeacherOption ? [currentUserHeadTeacherOption] : []),
                ]),
                useCurrentUserAsDefault: Boolean(currentUserHeadTeacherOption && staffItems.length === 0),
            }
        }

        const loadData = async () => {
            try{
                setError('')
                setIsLoadingOptions(true)

                const [teamsResponse, headTeacherResult] = await Promise.all([
                    teamService.list(workTgIds),
                    loadHeadTeacherOptions(),
                ])

                if(!isActual) return

                const nextTeamValues = teamsResponse.reduce<Record<number, ITeamFormValue>>((acc, team) => {
                    acc[Number(team.id)] = getTeamShiftValue(team, shift)
                    return acc
                }, {})

                setTeams(teamsResponse)
                setHeadTeacherOptions(headTeacherResult.items)
                setSelectedTeamIds(shift?.teams.length ? shift.teams.map(team => Number(team.teamId)).filter(Boolean) : teamsResponse.map(team => Number(team.id)))
                setTeamValues(nextTeamValues)
                setHeadTeacherId(
                    Number(shift?.headTeacher.plannedStaff?.userId ?? 0) ||
                    (headTeacherResult.useCurrentUserAsDefault ? Number(currentUserHeadTeacherOption?.id ?? 0) : 0)
                )
            }
            catch(e){
                handleError(e, 'Ошибка при получении данных для смены')
            }
            finally{
                if(isActual) setIsLoadingOptions(false)
            }
        }

        loadData()

        return () => {
            isActual = false
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, shift?.id, workTgIdsKey, currentUserHeadTeacherOption, shiftHeadTeacherOption])

    const onClose = (value: boolean) => {
        if(!value){
            setError('')
        }
        setOpen(value)
    }

    const setTeamCoordinator = (teamId: number, coordinatorUserId: number) => {
        setTeamValues(current => ({
            ...current,
            [teamId]: {
                ...(current[teamId] ?? {curatorUserIdsByTgId: {}}),
                coordinatorUserId,
            }
        }))
        setError('')
    }

    const setTeamCurator = (teamId: number, tgId: number, curatorUserId: number) => {
        setTeamValues(current => ({
            ...current,
            [teamId]: {
                ...(current[teamId] ?? {coordinatorUserId: 0, curatorUserIdsByTgId: {}}),
                curatorUserIdsByTgId: {
                    ...(current[teamId]?.curatorUserIdsByTgId ?? {}),
                    [tgId]: curatorUserId,
                }
            }
        }))
        setError('')
    }

    const getPayloadTeams = (): IShiftTeamInput[] | null => {
        if(!headTeacherId){
            setError('Выберите главрука смены')
            return null
        }

        if(!selectedTeamIds.length){
            setError('Выберите хотя бы одно звено')
            return null
        }

        const result: IShiftTeamInput[] = []

        for(const teamId of selectedTeamIds){
            const team = teams.find(item => Number(item.id) === teamId)
            const value = teamValues[teamId]

            if(!team || !value){
                setError('Не удалось собрать данные звена')
                return null
            }

            if(!value.coordinatorUserId){
                setError(`Выберите координатора для звена "${team.name}"`)
                return null
            }

            const curators = team.tgAccounts.map(tgAccount => ({
                tgAccountId: tgAccount.id,
                curatorUserId: String(value.curatorUserIdsByTgId[Number(tgAccount.id)] ?? 0),
            }))

            if(curators.some(curator => !Number(curator.curatorUserId))){
                setError(`Выберите кураторов для всех рабочих ТГ звена "${team.name}"`)
                return null
            }

            const curatorUserIds = curators.map(curator => curator.curatorUserId)
            if(new Set(curatorUserIds).size !== curatorUserIds.length){
                setError(`Для каждого рабочего ТГ звена "${team.name}" нужен отдельный куратор`)
                return null
            }

            result.push({
                teamId: String(teamId),
                coordinatorUserId: String(value.coordinatorUserId),
                curators,
            })
        }

        return result
    }

    const onSave = async () => {
        const teamsPayload = getPayloadTeams()
        if(!teamsPayload) return

        try{
            setIsSaving(true)
            const response = await shiftService.assignShift({
                date,
                headTeacherUserId: String(headTeacherId),
                teams: teamsPayload,
            })
            setGlobalMessage({message: shift ? 'Состав смены обновлён' : 'Смена назначена', type: 'ok'})
            onClose(false)
            onSuccess(response.shift)
        }
        catch(e){
            handleError(e, shift ? 'Ошибка при обновлении состава смены' : 'Ошибка при назначении смены')
        }
        finally{
            setIsSaving(false)
        }
    }

    return (
        <Modal open={open} setOpen={onClose}>
            <section className={classes.container}>
                <section className={classes.header}>
                    <section>
                        <h3>{title}</h3>
                        <p>Выберите главрука и звенья. Состав звена подставится автоматически, но его можно поправить только для этой даты.</p>
                    </section>
                    <button type="button" className={classes.closeButton} onClick={() => onClose(false)}>×</button>
                </section>

                {isLoadingOptions ? (
                    <section className={classes.loader}>
                        <LoaderSpinner />
                    </section>
                ) : (
                    <section className={classes.form}>
                        <section className={classes.mainFields}>
                            <section className={classes.field}>
                                <span className={classes.label}>Главрук смены</span>
                                <DropDownListSelected
                                    items={headTeacherOptions}
                                    selectedIdItems={headTeacherId ? [headTeacherId] : []}
                                    onSelected={(item) => (selected) => {
                                        setHeadTeacherId(selected ? Number(item.id) : 0)
                                        setError('')
                                    }}
                                    isLoading={false}
                                    search
                                    placeholder="Выберите главрука"
                                    searchPlaceholder="Поиск сотрудника"
                                />
                            </section>

                            <section className={classes.field}>
                                <span className={classes.label}>Звенья в смене</span>
                                <DropDownListSelected
                                    items={teams.map(team => ({id: Number(team.id), name: team.name || `Звено ${team.id}`}))}
                                    selectedIdItems={selectedTeamIdItems}
                                    onSelected={(item) => (selected) => {
                                        setSelectedTeamIds(current => selected
                                            ? [...current.filter(id => id !== Number(item.id)), Number(item.id)]
                                            : current.filter(id => id !== Number(item.id))
                                        )
                                        setError('')
                                    }}
                                    onSelectAll={(selectedIds) => {
                                        setSelectedTeamIds(selectedIds.map(Number))
                                        setError('')
                                    }}
                                    selectedCount
                                    isLoading={false}
                                    search
                                    placeholder="Выберите звенья"
                                    searchPlaceholder="Поиск звена"
                                    emptyText="Звенья не найдены"
                                />
                            </section>
                        </section>

                        <section className={classes.compositionHeader}>
                            <section>
                                <h4>Плановый состав смены</h4>
                                <p>Состав подтягивается из звена. Изменения применяются только к этой смене.</p>
                            </section>
                        </section>

                        <section className={classes.teams}>
                            {selectedTeamIds.map(teamId => {
                                const team = teams.find(item => Number(item.id) === teamId)
                                if(!team) return null

                                const teamValue = teamValues[teamId]
                                const coordinatorOptions = uniqueItems(team.coordinators.map(teamStaffToItem))
                                const defaultCoordinatorId = getDefaultCoordinatorId(team)
                                const isCoordinatorChanged = Boolean(
                                    teamValue?.coordinatorUserId &&
                                    defaultCoordinatorId &&
                                    teamValue.coordinatorUserId !== defaultCoordinatorId
                                )

                                return (
                                    <article className={classes.teamCard} key={teamId}>
                                        <section className={classes.teamHeader}>
                                            <section>
                                                <h4>{team.name || `Звено ${team.id}`}</h4>
                                                <p>{team.tgAccounts.map(tg => tg.title || tg.username).join(', ')}</p>
                                            </section>
                                            <span>из звена</span>
                                        </section>

                                        <section className={classes.assignmentBlock}>
                                            <section className={classes.assignmentTitle}>
                                                <span>Координатор звена</span>
                                                {isCoordinatorChanged && <b>изменён для смены</b>}
                                            </section>
                                            <section className={classes.field}>
                                                <DropDownListSelected
                                                    items={coordinatorOptions}
                                                    selectedIdItems={teamValue?.coordinatorUserId ? [teamValue.coordinatorUserId] : []}
                                                    onSelected={(item) => (selected) => setTeamCoordinator(teamId, selected ? Number(item.id) : 0)}
                                                    isLoading={false}
                                                    search
                                                    placeholder="Выберите координатора"
                                                    searchPlaceholder="Поиск координатора"
                                                    emptyText="Координаторы не найдены"
                                                />
                                            </section>
                                        </section>

                                        <section className={classes.curators}>
                                            {team.tgCurators.map(tgCurators => {
                                                const tgId = Number(tgCurators.tgAccount.id)
                                                const defaultCuratorId = getDefaultCuratorId(team, tgId)
                                                const selectedCuratorId = teamValue?.curatorUserIdsByTgId[tgId] ?? 0
                                                const selectedOtherCuratorIds = team.tgAccounts
                                                    .map(tgAccount => Number(tgAccount.id))
                                                    .filter(otherTgId => otherTgId !== tgId)
                                                    .map(otherTgId => teamValue?.curatorUserIdsByTgId[otherTgId] ?? 0)
                                                    .filter(Boolean)
                                                const curatorOptions = uniqueItems(
                                                    tgCurators.curators.map(teamStaffToItem)
                                                ).filter(item => (
                                                    Number(item.id) === selectedCuratorId
                                                        ||
                                                    !selectedOtherCuratorIds.includes(Number(item.id))
                                                ))
                                                const isCuratorChanged = Boolean(selectedCuratorId && defaultCuratorId && selectedCuratorId !== defaultCuratorId)

                                                return (
                                                    <section className={classes.assignmentBlock} key={tgId}>
                                                        <section className={classes.assignmentTitle}>
                                                            <span>Куратор {tgCurators.tgAccount.title || tgCurators.tgAccount.username}</span>
                                                            {isCuratorChanged && <b>изменён для смены</b>}
                                                        </section>
                                                        <section className={classes.field}>
                                                            <DropDownListSelected
                                                                items={curatorOptions}
                                                                selectedIdItems={selectedCuratorId ? [selectedCuratorId] : []}
                                                                onSelected={(item) => (selected) => setTeamCurator(teamId, tgId, selected ? Number(item.id) : 0)}
                                                                isLoading={false}
                                                                search
                                                                placeholder="Выберите куратора"
                                                                searchPlaceholder="Поиск куратора"
                                                                emptyText="Кураторы не найдены"
                                                            />
                                                        </section>
                                                    </section>
                                                )
                                            })}
                                        </section>
                                    </article>
                                )
                            })}

                            {!selectedTeamIds.length && (
                                <section className={classes.placeholder}>Выберите звенья, которые работают в эту смену</section>
                            )}
                        </section>
                    </section>
                )}

                <section className={classes.footer}>
                    <section className={classes.secondaryButton}>
                        <MyButtonSecondary onClick={() => onClose(false)}>
                            Отмена
                        </MyButtonSecondary>
                    </section>
                    <MyButton onClick={onSave} isLoading={isSaving || isLoadingOptions} error={error}>Сохранить</MyButton>
                </section>
            </section>
        </Modal>
    )
}
